import { ref, toRaw } from 'vue';
import { useToast } from './useToast';
import Modal from '@/utils/Modal';

/**
 * Composable for CRUD operations (replaces Addable, Editable, Removable mixins)
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.api - API service instance from useApi() (required for remove operations)
 * @param {Component} options.formComponent - Vue component for add/edit modal
 * @param {string} options.displayField - Field name used for display in confirm messages (default: 'name')
 * @param {Object} options.route - Vue Router route object (for redirect)
 * @param {Object} options.router - Vue Router instance (for redirect)
 * @param {Function} options.onAdded - Callback after item is added
 * @param {Function} options.onEdited - Callback after item is edited
 * @param {Function} options.onRemoved - Callback after item is removed
 */
export function useCrud(options = {}) {
    const { api, formComponent, displayField = 'name', route, router, onAdded, onEdited, onRemoved } = options;

    const toast = useToast();
    const rows = ref([]);
    const selected = ref({});

    /**
     * Open add modal and handle creation
     * @param {Object} defaultData - Optional default data for new item
     */
    function addItem(defaultData = {}) {
        if (!formComponent) {
            console.warn('useCrud: formComponent not provided');
            return Promise.reject(new Error('formComponent not provided'));
        }

        return Modal.show(formComponent, {
            data: defaultData
        })
            .then(row => {
                rows.value = [row, ...rows.value];

                if (typeof onAdded === 'function') {
                    onAdded(row);
                }

                toast.success(i18n.t('The item was created successfully!'));
                return row;
            })
            .catch(() => {});
    }

    /**
     * Open edit modal and handle update
     * @param {Object} data - Item data to edit
     */
    function editItem(data) {
        if (!formComponent) {
            console.warn('useCrud: formComponent not provided');
            return Promise.reject(new Error('formComponent not provided'));
        }

        selected.value = data;
        return Modal.show(formComponent, {
            data: structuredClone(toRaw(data))
        })
            .then(updatedRow => {
                // Update the selected reference
                for (const property in updatedRow) {
                    selected.value[property] = updatedRow[property];
                }

                // Update in rows array
                const idx = rows.value.findIndex(c => c.id === updatedRow.id);
                if (idx !== -1) {
                    rows.value.splice(idx, 1, updatedRow);
                }

                if (typeof onEdited === 'function') {
                    onEdited(updatedRow);
                }

                toast.success(i18n.t('The item was updated successfully!'));
                return updatedRow;
            })
            .catch(() => {});
    }

    /**
     * Confirm and remove a single item
     *
     * The wording is overridable per call because "remove" does not mean the
     * same thing on every screen — removing a member from an organization is
     * not deleting their account, and a generic dialog says the wrong thing.
     * When an option is absent the generic wording is used, so callers that
     * pass nothing behave exactly as before.
     *
     * @param {Object} row - Item to remove
     * @param {Object} removeOptions - Optional settings
     * @param {string} removeOptions.redirectRoute - Route name to redirect after delete
     * @param {string} removeOptions.confirmTitle - Title of the confirmation dialog
     * @param {string} removeOptions.confirmMessage - Body of the confirmation dialog
     * @param {string} removeOptions.successMessage - Toast shown when the server sends no message of its own
     */
    async function removeItem(row, removeOptions = {}) {
        if (!api) {
            console.warn('useCrud: api not provided');
            return Promise.reject(new Error('api not provided'));
        }

        const { redirectRoute, confirmTitle, confirmMessage, successMessage } = removeOptions;
        const itemName = row[displayField] || row.name || row.id;

        try {
            await Modal.confirm(
                confirmTitle ?? i18n.t('Confirm remove'),
                confirmMessage ?? i18n.t('Do you want to remove the item: {item}?', { item: itemName })
            );

            const response = await api.destroy(row.id);
            rows.value = rows.value.filter(c => c.id !== row.id);

            if (typeof onRemoved === 'function') {
                onRemoved(row);
            }

            // A 204 carries no body, so every existing caller lands on the
            // generic toast. An endpoint with something to say — "this person
            // keeps access through their platform role" — says it here.
            const serverMessage = response?.data?.message;
            if (serverMessage && response?.data?.level === 'warning') {
                toast.warning(serverMessage);
            } else {
                toast.success(serverMessage ?? successMessage ?? i18n.t('The item was deleted successfully!'));
            }

            if (redirectRoute && router) {
                const { subquery: _, ...query } = route?.query || {};
                router.replace({
                    name: redirectRoute,
                    query
                });
            }
        } catch (error) {
            if (error?.response) {
                new AxiosError(error.response).manage();
            }
            // Otherwise modal was cancelled
        }
    }

    /**
     * Remove multiple selected items
     * @param {Array} selectedIds - Array of IDs to remove
     * @param {Object} removeOptions - Optional settings
     * @param {string} removeOptions.redirectRoute - Route name to redirect after delete
     * @param {Function} removeOptions.onReset - Callback to reset list state
     */
    async function removeSelection(selectedIds, removeOptions = {}) {
        if (!api) {
            console.warn('useCrud: api not provided');
            return Promise.reject(new Error('api not provided'));
        }

        if (selectedIds.length === 0) return;

        const { redirectRoute, onReset } = removeOptions;

        try {
            await Modal.confirm(i18n.t('Confirm remove'), i18n.t('Do you want to remove the selected items?'));

            const ids = selectedIds.join(',');
            await api.destroy(ids);

            // If onReset callback provided, use it (for full list reset)
            if (typeof onReset === 'function') {
                onReset();
            } else {
                // Otherwise just filter out deleted items
                rows.value = rows.value.filter(row => !selectedIds.includes(row.id));
            }

            toast.success(i18n.t('The items were deleted successfully!'));

            if (redirectRoute && router) {
                const { subquery: _, ...query } = route?.query || {};
                router.push({
                    name: redirectRoute,
                    query
                });
            }
        } catch (error) {
            if (error?.response) {
                new AxiosError(error.response).manage();
            }
        }
    }

    return {
        rows,
        selected,
        addItem,
        editItem,
        removeItem,
        removeSelection
    };
}
